// The upload method's file checks, summary and joining (Area Builder Story 4), shared by the
// Create New Area panel and the geometry editor.
import type { VerifiedUpload } from 'types/area-geometry';
import { countText } from 'utils/number';

/** What the upload takes: a zipped shapefile or GeoJSON, as the server does. */
export const UPLOAD_ACCEPT = ['.zip', '.geojson', '.json'];

/** The server's own cap (AreaGeometryUpload::MAX_FILE_SIZE). */
export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

/**
 * Why a file is refused before it is sent: a drop hands over any file, whatever
 * the picker offers. The server checks both again.
 *
 * @param {File} file - The dropped or picked file.
 * @return {string | null} The reason, or null when it can go.
 */
export const uploadProblem = (file: File): string | null => {
  const name = file.name.toLowerCase();
  if (!UPLOAD_ACCEPT.some((extension) => name.endsWith(extension))) {
    return 'Unsupported file type. Upload a .zip shapefile or a .geojson file.';
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    return 'File is larger than 25 MB. Upload a smaller file.';
  }
  return null;
};

/**
 * The verified file's one-line summary, as the mock words it: the polygons kept,
 * then the skipped features counted by type. The server lists one line per
 * skipped feature ("Point feature skipped").
 *
 * @param {VerifiedUpload} upload - What the verify job found.
 * @param {string} kept - "verified" in Create New Area, "added" in the editor.
 * @return {string} "1 polygon verified", or "3 polygons added · ignored 2 Point, 1 LineString".
 */
export const uploadNote = (
  { verified, ignored }: VerifiedUpload,
  kept = 'verified'
): string => {
  const counts = new Map<string, number>();
  ignored.forEach((line) => {
    const type = line.replace(/ feature skipped$/, '').replace(/ skipped$/, '');
    counts.set(type, (counts.get(type) ?? 0) + 1);
  });
  const skipped = [...counts]
    .map(([type, count]) => `${countText(count)} ${type}`)
    .join(', ');
  const polygons = `${countText(verified)} polygon${verified === 1 ? '' : 's'} ${kept}`;
  return skipped ? `${polygons} · ignored ${skipped}` : polygons;
};

/**
 * Several verified files as one: their polygons side by side in one shape, the
 * counts summed. The server dissolves the shape, so overlaps across files merge.
 *
 * @param {VerifiedUpload[]} results - Each verified file's result.
 * @return {VerifiedUpload | null} The files together, or null when there are none.
 */
export const combineUploads = (
  results: VerifiedUpload[]
): VerifiedUpload | null => {
  if (results.length <= 1) return results[0] ?? null;
  return {
    verified: results.reduce((sum, { verified }) => sum + verified, 0),
    ignored: results.flatMap(({ ignored }) => ignored),
    geojson: {
      type: 'MultiPolygon',
      coordinates: results.flatMap(({ geojson }) =>
        geojson.type === 'Polygon' ? [geojson.coordinates] : geojson.coordinates
      ),
    },
  };
};
